import {
    useMutation,
    UseMutationOptions,
    useQueryClient,
    type UseMutateFunction,
} from '@tanstack/react-query'

import { InviteLinkWithAnalyticsType } from '@/shared/api'
import { InviteLink } from '@synapex/prisma/client'
import {
    CreateChannelAdminContactDto,
    DeleteInviteLinkResponse,
    GenerateInviteLinkDto,
    MoveInviteLinksDto,
    MoveInviteLinksResponse,
    UpdateInviteLinkDto,
} from '@synapex/shared'
import { CamelIdObj } from '@synapex/tools/typescript'

import { inviteLinksService } from './invite-links.service'
import { LinksQueryKeyConstants } from '../invite-links-group/invite-links-group.query.constants'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account/hooks'
import { channelLinkMany } from '@/true-fsd/data/link/react-query/key-fn'

//https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#via-the-cache
export function useDeleteInviteLinkMutation({
    channelId,
}: CamelIdObj<'channel'>) {
    const queryClient = useQueryClient()

    const channelLinksQueryKey = channelLinkMany({
        channel_id: channelId,
    }).queryKey

    return useMutation<
        DeleteInviteLinkResponse,
        Error,
        number,
        { backup: InviteLinkWithAnalyticsType[] }
    >({
        mutationFn: (id: number) => inviteLinksService.deleteInviteLink(id),
        onSuccess: (_data, variables, _context) => {
            console.log(`InviteLink ${variables} deleted successfully`)
        },
        onMutate: async (linkId) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: channelLinksQueryKey,
            })

            const backup =
                queryClient.getQueryData<InviteLinkWithAnalyticsType[]>(
                    channelLinksQueryKey
                ) ?? []

            // Optimistically update to the new value
            queryClient.setQueryData<InviteLinkWithAnalyticsType[]>(
                channelLinksQueryKey,
                (old) =>
                    old
                        ? old.filter((link) => link.invite_link_id !== linkId)
                        : []
            )

            // Return a context object with the snapshotted value
            return { backup }
        },
        onError: (error, variables, context) => {
            console.error(`InviteLink ${variables} deleting error`)
            console.error(error)
            if (context) {
                queryClient.setQueryData(channelLinksQueryKey, context.backup)
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: [LinksQueryKeyConstants.GroupsChannels, channelId],
            })
            void queryClient.invalidateQueries({
                queryKey: channelLinksQueryKey,
            })
        },
    })
}

export function useGenerateInviteLinkMutation(
    options?: UseMutationOptions<
        InviteLinkWithAnalyticsType,
        Error,
        GenerateInviteLinkDto
    >
) {
    const queryClient = useQueryClient()

    return useMutation<
        InviteLinkWithAnalyticsType,
        Error,
        GenerateInviteLinkDto
    >({
        mutationFn: (data: GenerateInviteLinkDto) =>
            inviteLinksService.generateInviteLink(data),
        ...options,
        onSuccess: (newInviteLink, variables, context) => {
            void queryClient.invalidateQueries({
                queryKey: channelLinkMany({
                    channel_id: variables.channelId,
                }).queryKey,
            })
            if (!newInviteLink.invite_link_id) {
                throw new Error('Ошибка при создании инвайт-ссылки')
            }
            if (options?.onSuccess) {
                options.onSuccess(newInviteLink, variables, context)
            }
        },
        onError: (error, variables, context) => {
            console.log('error, variables, context', error, variables, context)
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useUpdateInviteLinkMutation({
    channelId,
}: CamelIdObj<'channel'>) {
    const queryClient = useQueryClient()

    const channelLinksQueryKey = channelLinkMany({
        channel_id: channelId,
    }).queryKey

    return useMutation<
        InviteLink,
        Error,
        UpdateInviteLinkDto,
        { backup: InviteLinkWithAnalyticsType[] }
    >({
        mutationFn: (data: UpdateInviteLinkDto) => {
            return inviteLinksService.updateInviteLink(data)
        },
        onMutate: async (dto) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: channelLinksQueryKey,
            })

            const backup =
                queryClient.getQueryData<InviteLinkWithAnalyticsType[]>(
                    channelLinksQueryKey
                ) ?? []

            // Optimistically update to the new value
            queryClient.setQueryData<InviteLinkWithAnalyticsType[]>(
                channelLinksQueryKey,
                (old) =>
                    old
                        ? old.map((link) => {
                              if (link.invite_link_id === dto.inviteLinkId) {
                                  const update: Partial<InviteLinkWithAnalyticsType> =
                                      {}
                                  if (dto.inviteLinkStatus) {
                                      update.status = dto.inviteLinkStatus
                                  }

                                  if (dto.name) {
                                      update.invite_link_name = dto.name
                                  }

                                  if (dto.budget) {
                                      update.budget = dto.budget
                                  }

                                  if (Object.keys(update).length > 0) {
                                      return { ...link, ...update }
                                  }
                              }

                              return link
                          })
                        : []
            )

            // Return a context object with the snapshotted value
            return { backup }
        },
        onError: (error, variables, context) => {
            console.error(
                `InviteLink ${JSON.stringify(variables)} updating error`
            )
            console.error(error)
            if (context) {
                queryClient.setQueryData(channelLinksQueryKey, context.backup)
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: [LinksQueryKeyConstants.GroupsChannels, channelId],
            })
            void queryClient.invalidateQueries({
                queryKey: channelLinksQueryKey,
            })
        },
    })
}

export type InviteLinkMutate = UseMutateFunction<
    InviteLink,
    Error,
    UpdateInviteLinkDto
>

export function useMoveInviteLinkMutation(
    options?: UseMutationOptions<
        MoveInviteLinksResponse,
        Error,
        MoveInviteLinksDto
    >
) {
    const { activeAwd } = useActiveWorkspace()

    const queryClient = useQueryClient()

    return useMutation<MoveInviteLinksResponse, Error, MoveInviteLinksDto>({
        mutationFn: (moveDto: MoveInviteLinksDto) =>
            inviteLinksService.moveInviteLinks(moveDto),
        ...options,
        onSuccess: (data, variables, context) => {
            if (activeAwd.workspace_type === 'channel') {
                void queryClient.invalidateQueries({
                    queryKey: channelLinkMany({
                        channel_id: activeAwd.workspace_id,
                    }).queryKey,
                })
            }

            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useAddChannelAdminContactMutations(
    groupId: number,
    options?: UseMutationOptions<any, Error, CreateChannelAdminContactDto>
) {
    const { activeAwd } = useActiveWorkspace()

    const queryClient = useQueryClient()

    return useMutation<any, Error, CreateChannelAdminContactDto>({
        mutationFn: (dto: CreateChannelAdminContactDto) =>
            inviteLinksService.addAdminContact(
                dto.inviteLinkId,
                dto.telegramUsername,
                dto.shouldSelectAdmin
            ),
        ...options,
        onSuccess: (data, variables, context) => {
            if (activeAwd.workspace_type === 'channel') {
                void queryClient.invalidateQueries({
                    queryKey: channelLinkMany({
                        channel_id: activeAwd.workspace_id,
                    }).queryKey,
                })
            }
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useDeleteChannelAdminContactMutations(
    groupId: number,
    options?: UseMutationOptions<any, Error, any>
) {
    const { activeAwd } = useActiveWorkspace()

    const queryClient = useQueryClient()

    return useMutation<any, Error, any>({
        mutationFn: ({ inviteLinkId, adminContactId }) =>
            inviteLinksService.deleteAdminContact(inviteLinkId, adminContactId),
        ...options,
        onSuccess: (data, variables, context) => {
            if (activeAwd.workspace_type === 'channel') {
                void queryClient.invalidateQueries({
                    queryKey: channelLinkMany({
                        channel_id: activeAwd.workspace_id,
                    }).queryKey,
                })
            }
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}
